// Smooth Scroll Utility
// This ensures consistent smooth scrolling across all browsers and devices

export const initSmoothScroll = () => {
  // Enable smooth scrolling for the entire document
  document.documentElement.style.scrollBehavior = 'smooth';
  document.body.style.scrollBehavior = 'smooth';

  // Fix for Safari and older browsers
  if (!CSS.supports('scroll-behavior', 'smooth')) {
    // Polyfill for browsers that don't support smooth scrolling
    const smoothScrollPolyfill = (target, duration = 800) => {
      const targetElement = typeof target === 'string' ? document.getElementById(target) : target;
      if (!targetElement) return;

      const targetPosition = targetElement.offsetTop - 80; // Account for fixed navbar
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      const animation = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run = ease(timeElapsed, startPosition, distance, duration);
        window.scrollTo(0, run);
        if (timeElapsed < duration) requestAnimationFrame(animation);
      };

      const ease = (t, b, c, d) => {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t + b;
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };

      requestAnimationFrame(animation);
    };

    // Override default scrollIntoView for smooth behavior
    Element.prototype.scrollIntoView = function(options = {}) {
      if (options.behavior === 'smooth' || options === true) {
        smoothScrollPolyfill(this);
      } else {
        // Use native scrollIntoView for instant scrolling
        Element.prototype.scrollIntoView.call(this, options);
      }
    };
  }

  // Prevent scroll restoration on page reload
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  // Fix for mobile devices
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (isMobile) {
    // Disable momentum scrolling that can interfere with smooth scrolling
    document.body.style.webkitOverflowScrolling = 'touch';
    document.body.style.overflowScrolling = 'touch';
  }

  // Optimize scroll performance
  let ticking = false;
  const optimizeScrollPerformance = () => {
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Any scroll-related updates should go here
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  optimizeScrollPerformance();
};

// Utility function to scroll to a specific element
export const scrollToElement = (elementId, offset = 80) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.offsetTop - offset;
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    });
  }
};

// Utility function to scroll to top
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// Fix for anchor links
export const initAnchorLinks = () => {
  document.addEventListener('click', (e) => {
    const target = e.target.closest('a[href^="#"]');
    if (target) {
      e.preventDefault();
      const targetId = target.getAttribute('href').substring(1);
      scrollToElement(targetId);
    }
  });
};

// Initialize everything
export const initAllScrollFixes = () => {
  initSmoothScroll();
  initAnchorLinks();
  
  // Fix for React Router navigation
  window.addEventListener('popstate', () => {
    if (window.location.hash) {
      const targetId = window.location.hash.substring(1);
      setTimeout(() => scrollToElement(targetId), 100);
    }
  });
};